import { integrationMappingApi } from '@/api/integrationMapping.api';
import { roleAccountApi } from '@/api/roleAccount.api';

/**
 * Rewrites the specification's sample payloads to use codes that exist in the company being
 * looked at.
 *
 * The shipped samples carry illustrative codes — SUP-8842, CAT-DRY, CUS-1042. They are right for a
 * document a third party reads, and wrong for a test run here: the posting engine resolves a party
 * or category through its master mapping, so an unmapped code fails before the rule is reached. The
 * operator then reads "Supplier 'SUP-8842' is not mapped" and has learned nothing about the rule
 * they were trying to test.
 *
 * Codes come from the mappings themselves rather than from the supplier or customer tables, because
 * a mapping is exactly the thing the engine looks up. A code drawn from here is guaranteed to
 * resolve; one drawn from anywhere else only usually does.
 */

/**
 * Mapped external codes available in this company.
 *
 * `id` is always the mapping's externalId, never its externalCode, even though the code reads far
 * better. Category and location accounts are routed by a scope id that holds the externalId, so a
 * sample carrying the friendly code resolves to nothing. The name is kept alongside so the panel
 * can say which master it picked without putting an unusable value in the payload.
 */
export interface Ref { id: string; name: string }

export interface CompanySampleCodes {
  suppliers:         Ref[];
  customers:         Ref[];
  categories:        Ref[];
  locations:         Ref[];
  paymentModes:      Ref[];
  adjustmentReasons: Ref[];
}

export const EMPTY_CODES: CompanySampleCodes = {
  suppliers: [], customers: [], categories: [],
  locations: [], paymentModes: [], adjustmentReasons: [],
};

const MAPPED = 'Mapped';
const PAGE = 25;

const fetchType = async (masterType: string) => {
  try {
    const page = await integrationMappingApi.list({ masterType, status: MAPPED, pageSize: PAGE });
    return page.items;
  } catch {
    // A missing permission or an empty company must not break the test panel — it falls back to
    // the shipped sample, which is still a valid payload.
    return [];
  }
};

/**
 * Tender codes come from the payment-mode account map, not from the PaymentMethod mappings.
 *
 * A tender line resolves through cfg_payment_mode_account_map keyed on its own payment_mode_code,
 * and those codes only partly coincide with what the mappings carry — so a code drawn from the
 * mappings often resolves to nothing and the test fails on plumbing rather than on the rule. The
 * map is the thing being looked up, so ask it. Only modes that actually bind CLEARING qualify:
 * that is the role the tender line resolves first.
 */
const fetchTenderCodes = async (): Promise<Ref[]> => {
  try {
    const rows = await roleAccountApi.paymentModes();
    const clearing = rows.filter(r => r.accountRole === 'CLEARING' && !r.costCenterId);
    const seen = new Set<string>();
    return clearing
      .filter(r => !seen.has(r.paymentModeCode) && seen.add(r.paymentModeCode))
      .map(r => ({ id: r.paymentModeCode, name: r.paymentModeCode }));
  } catch {
    return [];
  }
};

export const loadCompanySampleCodes = async (): Promise<CompanySampleCodes> => {
  const [suppliers, customers, categories, locations, warehouses, modes, reasons] = await Promise.all([
    fetchType('Supplier'),
    fetchType('Customer'),
    fetchType('ProductCategory'),
    fetchType('Location'),
    fetchType('Warehouse'),
    fetchTenderCodes(),
    fetchType('StockAdjustmentReason'),
  ]);

  const toRef = (m: { externalId: string; externalName: string }): Ref =>
    ({ id: m.externalId, name: m.externalName });

  return {
    suppliers:  suppliers.map(toRef),
    customers:  customers.map(toRef),
    categories: categories.map(toRef),
    // Either master type maps to a cost centre, so either will do for references.locationExternalId.
    locations:  [...locations, ...warehouses].map(toRef),
    paymentModes:      modes,                 // already Refs — see fetchTenderCodes
    adjustmentReasons: reasons.map(toRef),
  };
};

export const hasAnyCodes = (c: CompanySampleCodes): boolean =>
  c.suppliers.length > 0 || c.customers.length > 0 || c.categories.length > 0 ||
  c.locations.length > 0 || c.paymentModes.length > 0 || c.adjustmentReasons.length > 0;

/** Distinct value per occurrence where there are enough to go round, so a two-tender sample does
 *  not settle twice through the same mode and collapse into one posting line. */
const pick = <T,>(pool: T[], seen: number): T | undefined =>
  pool.length === 0 ? undefined : pool[seen % pool.length];

/**
 * Substitutes by field name rather than by search-and-replace on the text: a blind replace of
 * "CAT-DRY" would also rewrite it inside a narration or a document number, and would miss a sample
 * that happens to use a different placeholder.
 */
export const applyCompanyCodes = (
  json: string,
  codes: CompanySampleCodes,
): { text: string; substituted: string[] } => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { text: json, substituted: [] };
  }

  const substituted = new Set<string>();
  const counts: Record<string, number> = {};
  const next = (key: string) => (counts[key] = (counts[key] ?? 0) + 1) - 1;

  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (node === null || typeof node !== 'object') return node;

    const obj = { ...(node as Record<string, unknown>) };

    // A party is the one case needing two fields kept consistent, and which pool to draw from
    // depends on the party's own type.
    if (typeof obj.externalId === 'string' && 'type' in obj) {
      const wantSupplier = String(obj.type).toLowerCase() === 'supplier';
      const pool = wantSupplier ? codes.suppliers : codes.customers;
      const hit = pick(pool, next('party'));
      if (hit) {
        obj.externalId = hit.id;
        if ('name' in obj) obj.name = hit.name;
        substituted.add(`${wantSupplier ? 'supplier' : 'customer'} (${hit.name})`);
      }
    }

    const simple: Array<[string, Ref[], string]> = [
      ['categoryExternalId',          codes.categories,        'category'],
      ['locationExternalId',          codes.locations,         'location'],
      ['modeCode',                    codes.paymentModes,      'payment mode'],
      ['adjustmentReasonExternalId',  codes.adjustmentReasons, 'adjustment reason'],
    ];
    for (const [field, pool, label] of simple) {
      if (typeof obj[field] === 'string') {
        const hit = pick(pool, next(field));
        if (hit) { obj[field] = hit.id; substituted.add(`${label} (${hit.name})`); }
      }
    }

    for (const [k, v] of Object.entries(obj)) obj[k] = walk(v);
    return obj;
  };

  return { text: JSON.stringify(walk(parsed), null, 2), substituted: [...substituted] };
};
